import React, { useEffect, useRef } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { tx } from '@/translations/translations';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───── PayPal Buttons wrapper (inside PayPalScriptProvider) ───── */
export const PayPalButtonsWrapper = ({ cartItems, total, address, couponCode, onSuccess, onError, language }) => {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  const cartRef = useRef(cartItems);
  const totalRef = useRef(total);
  const addressRef = useRef(address);
  const couponRef = useRef(couponCode);

  // Keep refs up to date so PayPal callbacks always use latest values
  useEffect(() => { cartRef.current = cartItems; }, [cartItems]);
  useEffect(() => { totalRef.current = total; }, [total]);
  useEffect(() => { addressRef.current = address; }, [address]);
  useEffect(() => { couponRef.current = couponCode; }, [couponCode]);

  if (isPending) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>{tx(language, 'Cargando PayPal...', 'Loading PayPal...', 'טוען PayPal...')}</span>
      </div>
    );
  }

  if (isRejected) {
    return (
      <div className="text-center py-4 text-red-500 text-sm">
        {tx(language, 'Error al cargar PayPal. Intenta recargar la página.', 'Failed to load PayPal. Try refreshing the page.', 'שגיאה בטעינת PayPal. נסו לרענן את הדף.')}
      </div>
    );
  }

  return (
    <PayPalButtons
      style={{ layout: 'vertical', color: 'blue', shape: 'rect', label: 'pay', height: 45 }}
      createOrder={async () => {
        try {
          const items = cartRef.current;
          const t = totalRef.current;
          const addr = addressRef.current;
          const res = await fetch(`${API_URL}/api/paypal/create-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: items.map(item => ({
                id: item.id, name: item.name, price: item.price, quantity: item.quantity,
                size: item.size || item.selectedSize || 'N/A',
                color: item.selectedColor?.name || null,
                tradition: item.selectedTradition || null,
                threads: item.selectedHilos || null,
                cottonType: item.selectedCottonType || null
              })),
              total: parseFloat(t.toFixed(2)),
              currency: 'MXN',
              shipping_address: addr,
              coupon_code: couponRef.current || null,
            })
          });
          if (!res.ok) {
            const errData = await res.text();
            console.error('PayPal create-order failed:', errData);
            throw new Error('Error creating order');
          }
          const data = await res.json();
          if (!data.id) throw new Error('No order ID returned');
          return data.id;
        } catch (err) {
          console.error('createOrder error:', err);
          onError(tx(language, 'Error al crear la orden de PayPal', 'Error creating PayPal order', 'שגיאה ביצירת הזמנת PayPal'));
          throw err;
        }
      }}
      onApprove={async (data) => {
        try {
          const res = await fetch(`${API_URL}/api/paypal/capture-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ order_id: data.orderID })
          });
          if (!res.ok) {
            const errData = await res.text();
            console.error('PayPal capture failed:', errData);
            throw new Error('Capture failed');
          }
          const result = await res.json();
          if (result.status === 'COMPLETED') {
            onSuccess('PayPal');
          } else {
            throw new Error('Payment status: ' + result.status);
          }
        } catch (err) {
          console.error('onApprove error:', err);
          onError(tx(language, 'Error al procesar el pago', 'Error processing payment', 'שגיאה בעיבוד התשלום'));
        }
      }}
      onError={(err) => {
        console.error('PayPal SDK error:', err);
        onError(tx(language, 'Error de PayPal. Intenta de nuevo.', 'PayPal error. Try again.', 'שגיאת PayPal. נסו שוב.'));
      }}
      onCancel={() => {
        toast.info(tx(language, 'Pago cancelado', 'Payment cancelled', 'התשלום בוטל'));
      }}
    />
  );
};
